import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  Snackbar,
} from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CloseIcon from '@material-ui/icons/Close';
import pagesClient from '../api/pagesClient';
import { getDashboardToken } from '../../utils/dashboardAuthParams';

const POLICIES = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only people signed into this Workload tenant',
  },
  {
    value: 'shared_link',
    label: 'Anyone with the link',
    description: 'Copy a link — no Workload account required',
  },
  {
    value: 'public',
    label: 'Public',
    description: 'Anyone on the internet with this page URL',
  },
];

function buildShareUrl(pageId, token) {
  if (typeof window === 'undefined') return '';
  const base = `${window.location.origin}/page/${pageId}`;
  return token ? `${base}?share=${encodeURIComponent(token)}` : base;
}

/**
 * Owner-facing share panel. Pages default to private; owners can switch to
 * shared_link (copyable token URL) or public. Hidden for anonymous / share viewers.
 */
export default function PageShareControls({ pageId, pageTenantId, initialPolicy = 'private' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [policy, setPolicy] = useState(initialPolicy || 'private');
  const [shareUrl, setShareUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setPolicy(initialPolicy || 'private');
  }, [initialPolicy]);

  // Session is enough to attempt manage — writes send this page's tenant_id as
  // X-Tenant-Id. Server still enforces JWT membership for that tenant. Do not
  // require localStorage `tenant` to match (users often have another tenant selected).
  const canManage = useMemo(() => {
    return Boolean(getDashboardToken() && pageTenantId);
  }, [pageTenantId]);

  const tenantOpts = useMemo(
    () => (pageTenantId ? { tenantId: pageTenantId } : {}),
    [pageTenantId]
  );

  const open = Boolean(anchorEl);

  const applyPolicy = useCallback(
    async (next) => {
      if (!pageId || next === policy) return;
      setBusy(true);
      setError('');
      try {
        await pagesClient.setVisibility(pageId, next, tenantOpts);
        setPolicy(next);
        if (next === 'shared_link') {
          const issued = await pagesClient.createShareToken(pageId, tenantOpts);
          const url =
            issued.share_url ||
            (typeof window !== 'undefined'
              ? `${window.location.origin}${issued.share_path || ''}`
              : '') ||
            buildShareUrl(pageId, issued.token);
          setShareUrl(url);
        } else {
          setShareUrl(next === 'public' ? buildShareUrl(pageId) : '');
        }
      } catch (e) {
        setError(e.message || 'Failed to update sharing');
      } finally {
        setBusy(false);
      }
    },
    [pageId, policy, tenantOpts]
  );

  const issueLink = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      if (policy !== 'shared_link') {
        await pagesClient.setVisibility(pageId, 'shared_link', tenantOpts);
        setPolicy('shared_link');
      }
      const issued = await pagesClient.createShareToken(pageId, tenantOpts);
      const url =
        issued.share_url ||
        (typeof window !== 'undefined'
          ? `${window.location.origin}${issued.share_path || ''}`
          : '') ||
        buildShareUrl(pageId, issued.token);
      setShareUrl(url);
    } catch (e) {
      setError(e.message || 'Failed to create share link');
    } finally {
      setBusy(false);
    }
  }, [pageId, policy, tenantOpts]);

  const writeClipboard = useCallback(async (url) => {
    if (!url) return false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(true);
      return true;
    } catch (e) {
      setError('Could not copy — select the link and copy manually');
      return false;
    }
  }, []);

  const copyLink = useCallback(async () => {
    let url = shareUrl;
    if (!url && policy === 'public') url = buildShareUrl(pageId);
    if (!url && policy === 'shared_link') {
      await issueLink();
      return;
    }
    if (!url) url = buildShareUrl(pageId);
    await writeClipboard(url);
  }, [shareUrl, policy, pageId, issueLink, writeClipboard]);

  if (!pageId) return null;

  return (
    <>
      <Box
        position="fixed"
        top={16}
        right={16}
        zIndex={1200}
        style={{ pointerEvents: 'auto' }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ShareIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          style={{ textTransform: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
        >
          Share
        </Button>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Paper style={{ width: 360, padding: 16 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Share this page
            </Typography>
            <IconButton size="small" onClick={() => setAnchorEl(null)} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {!canManage ? (
            <Typography variant="body2" color="textSecondary">
              Sign in via Automate so this browser has a Workload session
              (<code>tok_</code> in local storage) to change Private / link / Public.
            </Typography>
          ) : (
            <>
          <Typography variant="body2" color="textSecondary" paragraph>
            New dashboards start private. Choose who can open the published page.
          </Typography>

          <RadioGroup
            value={policy}
            onChange={(e) => applyPolicy(e.target.value)}
          >
            {POLICIES.map((p) => (
              <Box key={p.value} mb={1}>
                <FormControlLabel
                  value={p.value}
                  control={<Radio color="primary" disabled={busy} />}
                  label={
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {p.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {p.description}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </RadioGroup>

          {busy && (
            <Box display="flex" justifyContent="center" my={1}>
              <CircularProgress size={22} />
            </Box>
          )}

          {error ? (
            <Typography variant="caption" color="error" display="block" gutterBottom>
              {error}
            </Typography>
          ) : null}

          {policy === 'shared_link' && (
            <Box mt={1}>
              {shareUrl ? (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <TextField
                    value={shareUrl}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                    onFocus={(e) => e.target.select()}
                  />
                  <IconButton onClick={copyLink} aria-label="Copy link" color="primary">
                    <FileCopyOutlinedIcon />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={busy}
                  onClick={issueLink}
                  style={{ textTransform: 'none' }}
                >
                  Create share link
                </Button>
              )}
            </Box>
          )}

          {policy === 'public' && (
            <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
              <TextField
                value={buildShareUrl(pageId)}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <IconButton
                onClick={() => writeClipboard(buildShareUrl(pageId))}
                aria-label="Copy public link"
                color="primary"
              >
                <FileCopyOutlinedIcon />
              </IconButton>
            </Box>
          )}
            </>
          )}
        </Paper>
      </Popover>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Link copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
